import { HTMLAttributes, ReactNode } from 'react';
import { CheckCircle, AlertTriangle, AlertCircle, Info, X } from 'lucide-react';

export type AlertVariant = 'success' | 'warning' | 'danger' | 'info';

export interface AlertProps extends Omit<HTMLAttributes<HTMLDivElement>, 'title'> {
  variant?: AlertVariant;
  title?: ReactNode;
  /** Pokaż przycisk X zamykający. Wymaga onClose. */
  onClose?: () => void;
  icon?: ReactNode;
}

const VARIANT_MAP: Record<AlertVariant, { bg: string; border: string; text: string; icon: typeof CheckCircle }> = {
  success: { bg: 'bg-success-bg', border: 'border-success-border', text: 'text-success', icon: CheckCircle },
  warning: { bg: 'bg-warning-bg', border: 'border-warning-border', text: 'text-warning', icon: AlertTriangle },
  danger:  { bg: 'bg-danger-bg',  border: 'border-danger-border',  text: 'text-danger',  icon: AlertCircle },
  info:    { bg: 'bg-info-bg',    border: 'border-info-border',    text: 'text-info',    icon: Info },
};

/**
 * Inline banner komunikatu. Statyczny - nie znika sam. Do floating auto-hide
 * używaj <Toast> (useToast hook).
 */
export function Alert({
  variant = 'info',
  title,
  onClose,
  icon,
  className,
  children,
  ...rest
}: AlertProps) {
  const cfg = VARIANT_MAP[variant];
  const Icon = cfg.icon;
  return (
    <div
      role="alert"
      className={[
        'flex items-start gap-3 rounded-xl border px-4 py-3',
        cfg.bg, cfg.border,
        className,
      ].filter(Boolean).join(' ')}
      {...rest}
    >
      <span className={`inline-flex items-center justify-center w-5 h-5 shrink-0 mt-0.5 ${cfg.text} [&>svg]:w-full [&>svg]:h-full`} aria-hidden="true">
        {icon ?? <Icon />}
      </span>
      <div className="flex-1 min-w-0">
        {title && <div className={`text-sm font-semibold ${cfg.text} mb-0.5`}>{title}</div>}
        <div className="text-sm text-text-secondary leading-relaxed">{children}</div>
      </div>
      {onClose && (
        <button
          type="button"
          onClick={onClose}
          className="shrink-0 inline-flex items-center justify-center w-6 h-6 rounded-md text-text-muted hover:text-text-primary hover:bg-surface-hover/60 transition-colors"
          aria-label="Zamknij"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      )}
    </div>
  );
}
